import { designVariables } from "@studio3t/design-variables";
import {
  ButtonMediumPrimary,
  ButtonMediumSecondary,
} from "@studio3t/generic-components";
import type React from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import styled from "styled-components";
import {
  ModalBackdrop,
  ModalCard,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalTitle,
} from "@/components/ModalChrome";
import { ModalCloseButton } from "@/components/ModalCloseButton";
import { useModalFocus } from "@/hooks/useModalFocus";

const MODAL = designVariables.ui.modal;

interface ChatNameModalProps {
  isOpen: boolean;
  mode: "new" | "rename";
  defaultName?: string;
  onClose: () => void;
  onSave: (name: string) => void;
}

export const ChatNameModal: React.FC<ChatNameModalProps> = ({
  isOpen,
  mode,
  defaultName = "",
  onClose,
  onSave,
}) => {
  const [name, setName] = useState(defaultName);
  // Restores focus to the trigger on close; the input's autoFocus then takes
  // over while open.
  const modalRef = useModalFocus(isOpen);
  // The save button inside the form triggers both its own onClick and the
  // form's onSubmit — guard so one click saves exactly once.
  const submittedRef = useRef(false);
  const titleId = useId();
  const fieldId = useId();

  useEffect(() => {
    if (isOpen) {
      setName(defaultName);
      submittedRef.current = false;
    }
  }, [isOpen, defaultName]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const submit = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed || submittedRef.current) return;
    submittedRef.current = true;
    onSave(trimmed);
    onClose();
  }, [name, onSave, onClose]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      submit();
    },
    [submit],
  );

  if (!isOpen) return null;

  const title = mode === "new" ? "New chat" : "Rename chat";

  return (
    <ModalBackdrop onClick={onClose} role="presentation">
      <Modal
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid="chat-name-modal"
      >
        <ModalHeader>
          <ModalTitle id={titleId}>{title}</ModalTitle>
          <ModalCloseButton
            $top={MODAL.header.paddingh}
            $right={MODAL.paddingw}
            onClick={onClose}
            aria-label="Close modal"
          />
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor={fieldId}>Chat name *</Label>
            <Input
              id={fieldId}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Mask PII fields"
              data-testid="chat-name-input"
              autoFocus
            />
          </FormGroup>
        </Form>

        <ModalFooter>
          <ModalFooterActions>
            <ButtonMediumSecondary text="Cancel" onClick={onClose} />
            <ButtonMediumPrimary
              text={mode === "new" ? "Create" : "Rename"}
              onClick={submit}
              disabled={!name.trim()}
            />
          </ModalFooterActions>
        </ModalFooter>
      </Modal>
    </ModalBackdrop>
  );
};

const Modal = styled(ModalCard)`
  width: 100%;
  max-width: 420px;
`;

const Form = styled.form`
  padding: 0 ${MODAL.paddingw}px ${MODAL.paddingb}px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.semanticTextHeading};
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 4px;
  background: ${(p) => p.theme.cards1Background};
  color: ${(p) => p.theme.semanticTextBody};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.linkColor};
  }
`;

export default ChatNameModal;
